/**
 * Compute the completion status of each Annual Check-In wizard step based
 * on formData.
 *
 * Returns an array (one entry per step) of:
 *   'complete' — required fields are filled
 *   'partial'  — some data entered but required fields are missing
 *   'empty'    — no data entered at all
 */

function hasValue(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'boolean') return v === true;
  return false;
}

function anyTruthy(obj) {
  if (!obj || typeof obj !== 'object') return false;
  return Object.values(obj).some((v) => hasValue(v));
}

function contactStatus(data) {
  if (!data) return 'empty';
  const required = ['name', 'email', 'phone', 'shares_finances'];
  const allReq = required.every((k) => hasValue(data[k]));
  if (!anyTruthy(data)) return 'empty';
  return allReq ? 'complete' : 'partial';
}

function accountsStatus(data) {
  if (!data) return 'empty';
  const required = ['added_investment_accounts', 'beneficiaries_changed'];
  const allReq = required.every((k) => hasValue(data[k]));
  if (!anyTruthy(data)) return 'empty';
  return allReq ? 'complete' : 'partial';
}

function rygStatus(data) {
  if (!data) return 'empty';
  return hasValue(data.answer) ? 'complete' : 'empty';
}

function portfolioStatus(data) {
  if (!data) return 'empty';
  return hasValue(data.check_frequency) ? 'complete' : 'empty';
}

function scheduleStatus(data) {
  if (!data || typeof data !== 'object') return 'empty';

  const explicitStatus = String(data.meeting_status || '').trim().toLowerCase();
  if (explicitStatus === 'scheduled') return 'complete';
  if (explicitStatus === 'canceled' || explicitStatus === 'cancelled') return 'partial';

  if (data.booking_detected) return 'complete';

  const bookingInfo = data.booking_data?.bookingInfo;
  if (bookingInfo && String(bookingInfo.statusCode || '').trim() === '1') {
    return 'complete';
  }
  if (Array.isArray(bookingInfo?.MeetingTimes) && bookingInfo.MeetingTimes.length > 0) {
    return 'complete';
  }

  return anyTruthy(data) ? 'partial' : 'empty';
}

export function computeStepStatuses(steps, formData) {
  return steps.map((step) => {
    const data = formData?.[step.key];
    switch (step.key) {
      case 'schedule_meeting':
        return scheduleStatus(data);
      case 'welcome':
      case 'review':
        return 'complete';
      case 'contact':
        return contactStatus(data);
      case 'accounts_beneficiaries':
        return accountsStatus(data);
      case 'risk_awareness':
      case 'investment_alignment':
      case 'growth_satisfaction':
      case 'investment_care':
        return rygStatus(data);
      case 'portfolio_monitoring':
        return portfolioStatus(data);
      default:
        return anyTruthy(data) ? 'complete' : 'empty';
    }
  });
}
